import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { tenantService } from '../services/tenantService';

const CreateTenantAccountModal = ({ isOpen, onClose, properties, onTenantCreated }) => {
  const { currentUser } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdTenant, setCreatedTenant] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyId: '',
    leaseStartDate: '',
    leaseEndDate: '',
    monthlyRent: '',
    defaultPassword: '',
    confirmPassword: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate form data
      if (!formData.name.trim()) {
        throw new Error(t('dashboard.owner.modals.createTenantAccount.errors.tenantNameRequired'));
      }
      if (!formData.email.trim()) {
        throw new Error(t('dashboard.owner.modals.createTenantAccount.errors.emailRequired'));
      }
      if (!formData.phone.trim()) {
        throw new Error(t('dashboard.owner.modals.createTenantAccount.errors.phoneRequired'));
      }
      if (!formData.propertyId) {
        throw new Error(t('dashboard.owner.modals.createTenantAccount.errors.propertyRequired'));
      }
      if (!formData.leaseStartDate) {
        throw new Error(t('dashboard.owner.modals.createTenantAccount.errors.leaseStartRequired'));
      }
      if (!formData.leaseEndDate) {
        throw new Error(t('dashboard.owner.modals.createTenantAccount.errors.leaseEndRequired'));
      }
      if (!formData.monthlyRent) {
        throw new Error(t('dashboard.owner.modals.createTenantAccount.errors.rentRequired'));
      }
      if (!formData.defaultPassword) {
        throw new Error(t('dashboard.owner.modals.createTenantAccount.errors.passwordRequired'));
      }
      if (formData.defaultPassword !== formData.confirmPassword) {
        throw new Error(t('dashboard.owner.modals.createTenantAccount.errors.passwordsMismatch'));
      }
      if (formData.defaultPassword.length < 6) {
        throw new Error(t('dashboard.owner.modals.createTenantAccount.errors.passwordTooShort'));
      }

      // Get selected property details
      const selectedProperty = properties.find(p => p.id === formData.propertyId);
      if (!selectedProperty) {
        throw new Error(t('dashboard.owner.modals.createTenantAccount.errors.selectedPropertyNotFound'));
      }

      // Create tenant account
      const result = await tenantService.createTenantAccount({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        propertyId: formData.propertyId,
        ownerId: currentUser.uid,
        leaseStartDate: formData.leaseStartDate,
        leaseEndDate: formData.leaseEndDate,
        monthlyRent: parseFloat(formData.monthlyRent),
        defaultPassword: formData.defaultPassword
      });

      // Set created tenant data for success modal
      setCreatedTenant({
        ...result,
        name: formData.name,
        propertyName: selectedProperty.name,
        propertyAddress: selectedProperty.address
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        propertyId: '',
        leaseStartDate: '',
        leaseEndDate: '',
        monthlyRent: '',
        defaultPassword: '',
        confirmPassword: ''
      });

      // Show success modal
      setShowSuccess(true);
      setLoading(false);

      if (onTenantCreated) {
        onTenantCreated(result);
      }
    } catch (error) {
      console.error('Error creating tenant account:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  const copyCredentials = async () => {
    try {
      const credentials = `Login Credentials for ${createdTenant.name}:\n\nEmail: ${createdTenant.email}\nPassword: ${createdTenant.defaultPassword}\n\nProperty: ${createdTenant.propertyName}\nAddress: ${createdTenant.propertyAddress}\n\nPlease change your password on first login.`;
      await navigator.clipboard.writeText(credentials);
      
      // Show temporary success message
      const copyButton = document.getElementById('copy-credentials-btn');
      if (copyButton) {
        const originalText = copyButton.textContent;
        copyButton.textContent = t('dashboard.owner.modals.createTenantAccount.success.credentialsCopied');
        copyButton.className = 'px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors';
        
        setTimeout(() => {
          copyButton.textContent = originalText;
          copyButton.className = 'px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors';
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to copy credentials:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = `Login Credentials for ${createdTenant.name}:\n\nEmail: ${createdTenant.email}\nPassword: ${createdTenant.defaultPassword}\n\nProperty: ${createdTenant.propertyName}\nAddress: ${createdTenant.propertyAddress}\n\nPlease change your password on first login.`;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      // Show success message
      const copyButton = document.getElementById('copy-credentials-btn');
      if (copyButton) {
        const originalText = copyButton.textContent;
        copyButton.textContent = t('dashboard.owner.modals.createTenantAccount.success.credentialsCopied');
        copyButton.className = 'px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors';
        
        setTimeout(() => {
          copyButton.textContent = originalText;
          copyButton.className = 'px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors';
        }, 2000);
      }
    }
  };

  const handleClose = () => {
    setShowSuccess(false);
    setCreatedTenant(null);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  // Filter to show only vacant properties
  const vacantProperties = properties.filter(property => property.status === 'vacant');

  // Success Modal
  if (showSuccess && createdTenant) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
        <div className="relative top-10 mx-auto p-6 border w-full max-w-2xl shadow-2xl rounded-2xl bg-white">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {t('dashboard.owner.modals.createTenantAccount.success.title')}
            </h3>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <h4 className="font-medium text-gray-900 mb-2">
                {t('dashboard.owner.modals.createTenantAccount.success.credentials')}
              </h4>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span className="font-medium">{createdTenant.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Email:</span>
                  <span className="font-medium">{createdTenant.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Password:</span>
                  <span className="font-medium">{createdTenant.defaultPassword}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Property:</span>
                  <span className="font-medium">{createdTenant.propertyName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Address:</span>
                  <span className="font-medium">{createdTenant.propertyAddress}</span>
                </div>
              </div>
            </div>
            
            <p className="text-gray-600 mb-6">
              {t('dashboard.owner.modals.createTenantAccount.success.shareInstructions')}
            </p>
            
            <div className="flex justify-center space-x-3">
              <button
                id="copy-credentials-btn"
                onClick={copyCredentials}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t('dashboard.owner.modals.createTenantAccount.success.copyCredentials')}
              </button>
              <button
                onClick={handleClose}
                className="px-4 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors"
              >
                {t('dashboard.owner.modals.createTenantAccount.success.close')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-6 border w-full max-w-2xl shadow-2xl rounded-2xl bg-white">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-800">
            {t('dashboard.owner.modals.createTenantAccount.title')}
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Tenant Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.owner.modals.createTenantAccount.form.tenantName')} *
              </label>
              <input
                type="text"
                name="name"
                required
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                placeholder={t('dashboard.owner.modals.createTenantAccount.form.tenantNamePlaceholder')}
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.owner.modals.createTenantAccount.form.emailAddress')} *
              </label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                placeholder={t('dashboard.owner.modals.createTenantAccount.form.emailPlaceholder')}
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.owner.modals.createTenantAccount.form.phoneNumber')} *
              </label>
              <input
                type="tel"
                name="phone"
                required
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                placeholder={t('dashboard.owner.modals.createTenantAccount.form.phonePlaceholder')}
              />
            </div>

            {/* Property Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.owner.modals.createTenantAccount.form.property')} *
              </label>
              <select
                name="propertyId"
                required
                value={formData.propertyId}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
              >
                <option value="">{t('dashboard.owner.modals.createTenantAccount.form.propertyPlaceholder')}</option>
                {vacantProperties.map((property) => (
                  <option key={property.id} value={property.id}>
                    {property.name} - {property.address}
                  </option>
                ))}
              </select>
              {vacantProperties.length === 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  {t('dashboard.owner.modals.createTenantAccount.errors.noVacantProperties')}
                </p>
              )}
            </div>

            {/* Lease Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.owner.modals.createTenantAccount.form.leaseStartDate')} *
              </label>
              <input
                type="date"
                name="leaseStartDate"
                required
                value={formData.leaseStartDate}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
              />
            </div>

            {/* Lease End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.owner.modals.createTenantAccount.form.leaseEndDate')} *
              </label>
              <input
                type="date"
                name="leaseEndDate"
                required
                value={formData.leaseEndDate}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
              />
            </div>

            {/* Monthly Rent */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.owner.modals.createTenantAccount.form.monthlyRent')} *
              </label>
              <input
                type="number"
                name="monthlyRent"
                required
                min="0"
                step="0.01"
                value={formData.monthlyRent}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                placeholder={t('dashboard.owner.modals.createTenantAccount.form.rentPlaceholder')}
              />
            </div>

            {/* Default Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.owner.modals.createTenantAccount.form.defaultPassword')} *
              </label>
              <input
                type="password"
                name="defaultPassword"
                required
                value={formData.defaultPassword}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                placeholder={t('dashboard.owner.modals.createTenantAccount.form.passwordPlaceholder')}
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('dashboard.owner.modals.createTenantAccount.form.passwordHint')}
              </p>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('dashboard.owner.modals.createTenantAccount.form.confirmPassword')} *
              </label>
              <input
                type="password"
                name="confirmPassword"
                required
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                placeholder={t('dashboard.owner.modals.createTenantAccount.form.confirmPlaceholder')}
              />
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h4 className="text-sm font-medium text-blue-800 mb-1">
                  {t('dashboard.owner.modals.createTenantAccount.info.title')}
                </h4>
                <p className="text-sm text-blue-700">
                  {t('dashboard.owner.modals.createTenantAccount.info.description')}
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
              disabled={loading}
            >
              {t('dashboard.owner.modals.createTenantAccount.buttons.cancel')}
            </button>
            <button
              type="submit"
              className="px-6 py-3 text-sm font-medium text-white bg-green-500 rounded-lg hover:bg-green-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || vacantProperties.length === 0}
            >
              {loading ? t('dashboard.owner.modals.createTenantAccount.buttons.creating') : t('dashboard.owner.modals.createTenantAccount.buttons.createAccount')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTenantAccountModal; 